import { z } from "zod";
import { type InferPassthrough, zKeyedRecord } from "../zod/utils.js";

import {
  FilledDynamicValueTableColumnType,
  FilledDynamicValueTableColumnTypeLiteral,
} from "../DynamicValue.js";
import { HqlAggregationFunctionLiteral } from "../hql/types.js";
import { DisplayTableColumnId } from "../idTypeBrands.js";
import { getNormalEnum } from "../pseudoEnums.js";

import { CalcColumnDefinition } from "./calcTypes.js";
import { ConditionalFormattingDefinitionWithMetadata } from "./conditionalFormattingTypes.js";

export const SortDirectionLiteral = z.enum(["ASC", "DESC"]);

export type SortDirection = z.infer<typeof SortDirectionLiteral>;
export const SortDirection = getNormalEnum(SortDirectionLiteral);
export type SortDirectionArray = ReadonlyArray<SortDirection | null> | null;

export const DisplayTableOutputColumnTypeLiteral =
  FilledDynamicValueTableColumnTypeLiteral;
export type DisplayTableColumnOutputType = z.infer<
  typeof FilledDynamicValueTableColumnTypeLiteral
>;
export const DisplayTableColumnOutputType = FilledDynamicValueTableColumnType;

export const DisplayTableOutputColumn = z
  .object({
    // An identifier used to reference this column. Used for example in fields of
    // DisplayTableConfig to reference columns.
    columnId: DisplayTableColumnId,

    // The name of the column, which may be a list/tuple in the case of hierarchical
    // columns in a MultiIndex DataFrame. The length of the list will always match
    // the length of columnLevelNames on the containing DisplayTableOutput
    columnName: z.array(z.string()),

    columnType: DisplayTableOutputColumnTypeLiteral,
    columnData: z.array(z.union([z.string(), z.null(), z.undefined()])),
  })
  .passthrough();
export type DisplayTableOutputColumn = InferPassthrough<
  typeof DisplayTableOutputColumn
>;

const DisplayTableOutputRequiredFields = z.object({
  rowCount: z.number(),

  columns: z.array(DisplayTableOutputColumn),

  // a list of names for each level of the column index of the DataFrame
  // names can be null but the length of the array will always match the
  // length of each DisplayTableOutputColumn's columnName array
  columnLevelNames: z.array(z.union([z.string(), z.null()])),

  indices: z.union([
    z.array(
      z.union([
        z.string(),
        z.number(),
        z.array(z.union([z.string(), z.number()])),
      ]),
    ),
    z.undefined(),
  ]),

  // a list of names for each level of the row index of the DataFrame
  indexLevelNames: z.array(z.union([z.string(), z.null()])),

  // in the case that an update pageSize causes the current pageNumber
  // to be invalid, this lets the python indicate which page number
  // it chose to show instead
  pageNumberOverride: z.union([z.number(), z.null(), z.undefined()]),

  // These following fields _could_ be read/derived
  // from the display table cell and display table session state,
  // however, we mirror them here to make sure the rendered
  // output is always consistent. The reason being is these
  // fields feel very odd if updated before the table is recomputed
  pageSize: z.number(),

  // Please do not add any more fields to this record, all new fields must go in DisplayTableOutputOptionalFields
  // for backwards compatibility purposes. See https://www.notion.so/hexhq/Avoiding-breaking-changes-59f0a1d8f34546249eadb17605822290
  // form more context
});
const DisplayTableWarningTypeLiteral = z.enum([
  "CONDITIONAL_FORMATTING_ERROR",
  "ORDER_NOT_PRESERVED",
]);

export type DisplayTableWarningType = z.infer<
  typeof DisplayTableWarningTypeLiteral
>;
export const DisplayTableWarningType = getNormalEnum(
  DisplayTableWarningTypeLiteral,
);

const DisplayTableWarning = z.object({
  type: DisplayTableWarningTypeLiteral,
  message: z.string(),
});
export type DisplayTableWarning = z.infer<typeof DisplayTableWarning>;

export const ColumnAggregationsOutput = zKeyedRecord(
  DisplayTableColumnId,
  z.object({
    result: z.union([z.number(), z.string()]),
    func: HqlAggregationFunctionLiteral,
  }),
);
export type ColumnAggregationsOutput = z.infer<typeof ColumnAggregationsOutput>;

export const DisplayTableExploreOutputType = z.enum([
  "source", // this is now unused
  "pivot", // this is the pivot table visualization
  "aggregated", // this is the nested table
]);
export type DisplayTableExploreOutputType = z.infer<
  typeof DisplayTableExploreOutputType
>;

const DisplayTableOutputOptionalFields = z
  .object({
    calcColumns: z.union([z.array(CalcColumnDefinition), z.null()]),
    // This field also has extra metadata that the BE adds in addition to copying
    // the conditionalFormatting definition from the display table cell.
    // Additionally, the arg field on ConditionalFormattingBinaryExpressions will
    // have been evaluated as Jinja templates in the output.
    conditionalFormatting: z.union([
      ConditionalFormattingDefinitionWithMetadata,
      z.null(),
    ]),

    configId: z.union([z.string(), z.null()]),

    // size of the DF in bytes
    dfSize: z.number(),

    // if the display table was truncated, this will be larger than the rowCount
    rowCountBeforeTruncation: z.number(),

    // If rowCountOfPreview is true, then the rowCount is the row count of the preview
    // page rather than the row count of the whole query
    rowCountOfPreview: z.boolean(),

    // Whether the table is in query mode
    queryMode: z.boolean(),

    // Issues with the table that don't rise to the level of an error.
    warnings: z.union([z.array(DisplayTableWarning), z.null()]),
    // true if sql display table failed due to sql query error and fell back to
    // python display table to render the table
    sqlErrorFallback: z.boolean(),
    // Note this field is not part of the backend DisplayTableOutput, but is output
    // from a separate stage and merged with this object in the frontend
    columnAggregations: ColumnAggregationsOutput,

    // Used exclusively for the Explore cell to specify which type of table output
    // this is. The Explore cell can render multiple types of table at the same time
    // and this field is used to differentiate between them.
    exploreOutputType: z.union([DisplayTableExploreOutputType, z.null()]),
  })

  .partial();

export const DisplayTableOutput = DisplayTableOutputOptionalFields.and(
  DisplayTableOutputRequiredFields,
);

export type DisplayTableOutput = InferPassthrough<typeof DisplayTableOutput>;

export const DISPLAY_TABLE_DEFAULTS = {
  pageSize: 50,
  hideIcons: false,
  hideIndex: false,
  sortDirectionDefault: SortDirection.ASC,
  height: null,
  pinIndexColumns: false,
  showAggregations: false,
} as const;
